/** What a lap did at each corner: braking, turn-in, minimum speed, throttle,
 *  and how far the car was rotated against its own direction of travel.
 *
 * One definition of each, shared by everything that talks about a corner —
 * the logger's race engineer (#110), its lap analysis document (#115) and
 * the corner report card. Before this the braking point lived in the
 * coaching detector alone, and was "the first brake application in the
 * 250 m before the corner's entry". On a real circuit that is often another
 * corner's: through a sequence the window before turn 13 holds turn 11's
 * braking zone, and a brake application that began after the entry marker
 * (an authored corner's window is the apex ± 75 m, not where the driver
 * turned in) was not found at all. On the best laps of nine stored sessions
 * it disagreed with where the corner's braking actually began at 41 of 105
 * corners — 13 of Mount Panorama's 23.
 *
 * So braking is attributed the other way round. The lap's brake applications
 * are found first, each is given to ONE corner — the first apex its midpoint
 * has not yet reached — and a corner's braking zone is the application, of
 * those it was given, that took the most speed off. The midpoint rather than
 * the onset, so that trail braking carried a few metres past an apex still
 * belongs to that apex and a stab begun a few metres before a kink belongs to
 * the corner it was really for.
 *
 * Everything here is measured on whatever distance axis the samples carry. To
 * compare two laps the caller puts both on one axis first
 * (`alignToReference`); the corner windows are the reference lap's, so every
 * lap is measured through the same stretch of road.
 *
 * Distances are metres along that axis, speeds km/h, pedals percent, angles
 * degrees.
 */

import { column } from './columns';
import { bisectLeft, bisectRight } from './interp';
import type { BrakeRun, Corner, CornerMeasure, Samples, Window } from './types';

/** The pedal position that counts as braking — the gate the logger's event
 *  detector uses for a lockup, so "braking" means one thing everywhere. */
export const BRAKE_ON_PCT = 20;
/** How far before a corner's entry its braking zone may begin. A brake
 *  application further back than this is about something else (traffic, a
 *  mistake on the straight) and belongs to no corner. */
export const BRAKE_SEARCH_M = 250;
/** A brake application shorter than this is a brush of the pedal, not a zone. */
export const BRAKE_MIN_S = 0.1;
/** Two applications this close together are one: the pedal dipping under
 *  the gate for a moment while the driver modulates it. */
export const BRAKE_MERGE_GAP_M = 10;

/** The throttle position that counts as "back on the power", and as flat. */
export const THROTTLE_ON_PCT = 20;
export const THROTTLE_FULL_PCT = 98;
/** How far past a corner's exit the throttle points are still looked for.
 *  Never past the next corner's apex, whatever this says. */
export const THROTTLE_SEARCH_M = 100;

/** Turn-in is where the yaw rate climbs through this share of the corner's
 *  peak on the way to it. A share rather than a figure because a hairpin
 *  and a fast sweeper peak an order of magnitude apart. */
export const TURN_IN_YAW_SHARE = 0.25;
/** Below this the car is not turning, whatever share of the peak it is
 *  (rad/s; a corner whose PEAK is under it has no turn-in to find). */
export const TURN_IN_YAW_FLOOR = 0.03;
/** How far before the apex turn-in is looked for. */
export const TURN_IN_SEARCH_M = 200;

/** The fewest samples a corner's body slip is summed up from. A corner the
 *  lap crossed in a handful of ticks has a reading, not a balance. */
export const SLIP_MIN_SAMPLES = 5;

/** Whether a nibble of the gear byte names a forward gear. Both count gears
 *  from 1; 0 is reverse, and 15 is neutral in the current gear's and "no
 *  suggestion" in the suggested gear's. Anything that compares gears treats
 *  every other value as no gear at all. */
export function isForwardGear(value: number): boolean {
  return value >= 1 && value <= 14;
}

/** A corner stitched across the start line: its entry is at the end of the
 *  lap and its exit at the start of the next. */
export function wraps(window: Window): boolean {
  return window.entry > window.exit;
}

/** A corner with the same keys `cornersForLap` answers, or a hand-built one
 *  with only `n`, `entry_dist` and `exit_dist`. */
export type WindowSource = Pick<Corner, 'n' | 'entry_dist' | 'exit_dist'> &
  Partial<Pick<Corner, 'apex_dist' | 'direction'>>;

/** The corners of a lap (`cornersForLap`'s, or any list with the same keys)
 *  as search windows, in the order they are driven.
 *
 * A corner without an `apex_dist` — the hand-built ones in tests, and
 * nothing `cornersForLap` returns — takes the middle of its window.
 */
export function windows(corners: readonly WindowSource[]): Window[] {
  const placed: { apex: number; n: number; entry: number; exit: number; direction: string }[] = [];
  for (const corner of corners) {
    const entry = Number(corner.entry_dist);
    const exit = Number(corner.exit_dist);
    let apex = corner.apex_dist;
    if (apex === undefined || apex === null) apex = entry <= exit ? (entry + exit) / 2 : exit;
    const direction = String(corner.direction || '');
    placed.push({ apex: Number(apex), n: Math.trunc(Number(corner.n)), entry, exit, direction });
  }
  // Python sorts the tuples (apex, n, entry, exit, direction) whole.
  placed.sort(
    (a, b) =>
      a.apex - b.apex ||
      a.n - b.n ||
      a.entry - b.entry ||
      a.exit - b.exit ||
      (a.direction < b.direction ? -1 : a.direction > b.direction ? 1 : 0),
  );
  return placed.map((p, i) => ({
    n: p.n,
    entry: p.entry,
    apex: p.apex,
    exit: p.exit,
    prev_apex: i > 0 ? placed[i - 1]!.apex : null,
    next_apex: i + 1 < placed.length ? placed[i + 1]!.apex : null,
    direction: p.direction,
  }));
}

/** A lap's samples on a strictly increasing distance axis, with its brake
 *  applications found once.
 *
 * Strictly increasing because distance is the axis everything is looked up
 * on: a pause, an import or an aligned lap holding its place through a
 * spin repeats a distance, and a repeated distance has no single answer to
 * "what was the speed there". The first sample at each distance is kept,
 * as it is everywhere else distance is an axis.
 */
export class LapTrace {
  readonly dist: number[];
  readonly runs: BrakeRun[];
  private readonly keep: number[];
  private readonly samples: Samples;
  private readonly columns = new Map<string, number[] | null>();

  constructor(samples: Samples) {
    const dist = column(samples, 'dist') ?? [];
    const keep: number[] = [];
    let last = -Infinity;
    for (let i = 0; i < dist.length; i++) {
      const d = dist[i]!;
      if (d > last) {
        keep.push(i);
        last = d;
      }
    }
    this.keep = keep;
    this.samples = samples;
    this.dist = keep.map((i) => dist[i]!);
    this.runs = this.brakeRuns();
  }

  get length(): number {
    return this.dist.length;
  }

  /** A column on the trace's axis, or null when the lap has no such channel
   *  (or not all of it). */
  column(name: string): number[] | null {
    const cached = this.columns.get(name);
    if (cached !== undefined) return cached;
    const raw = column(this.samples, name);
    const keep = this.keep;
    const usable = raw !== undefined && keep.length > 0 && raw.length > keep[keep.length - 1]!;
    const value = usable && raw.length > 0 ? keep.map((i) => raw[i]!) : null;
    this.columns.set(name, value);
    return value;
  }

  /** The first sample at or past `dist`, clamped to the lap. */
  index(dist: number): number {
    return Math.min(Math.max(bisectLeft(this.dist, dist), 0), Math.max(this.dist.length - 1, 0));
  }

  /** Indices of the samples with lo <= dist <= hi, as [start, end). */
  span(lo: number, hi: number): [number, number] {
    return [bisectLeft(this.dist, lo), bisectRight(this.dist, hi)];
  }

  /** A column's value at a distance, interpolated. null off the ends of the
   *  lap: the value a little past the last sample is not the last sample's. */
  at(name: string, dist: number): number | null {
    const col = this.column(name);
    const d = this.dist;
    if (col === null || d.length === 0 || dist < d[0]! || dist > d[d.length - 1]!) return null;
    const i = bisectLeft(d, dist);
    if (d[i] === dist || i === 0) return col[i]!;
    const x0 = d[i - 1]!;
    const x1 = d[i]!;
    return col[i - 1]! + ((col[i]! - col[i - 1]!) * (dist - x0)) / (x1 - x0);
  }

  private brakeRuns(): BrakeRun[] {
    const brake = this.column('brake');
    const d = this.dist;
    if (brake === null || d.length < 2) return [];
    const t = this.column('t');
    const raw: [number, number][] = [];
    let start: number | null = null;
    for (let i = 0; i < brake.length; i++) {
      if (brake[i]! >= BRAKE_ON_PCT) {
        if (start === null) start = i;
      } else if (start !== null) {
        raw.push([start, i - 1]);
        start = null;
      }
    }
    if (start !== null) raw.push([start, brake.length - 1]);

    const merged: [number, number][] = [];
    for (const [on, off] of raw) {
      const prev = merged[merged.length - 1];
      if (prev !== undefined && d[on]! - d[prev[1]]! < BRAKE_MERGE_GAP_M) {
        prev[1] = off;
      } else {
        merged.push([on, off]);
      }
    }

    const runs: BrakeRun[] = [];
    for (const [on, off] of merged) {
      // By the clock where there is one; a lap without it (nothing the
      // logger records) is held to two samples instead.
      const longEnough = t !== null ? t[off]! - t[on]! >= BRAKE_MIN_S : off > on;
      if (longEnough) runs.push({ on, off, onset_known: on > 0 });
    }
    return runs;
  }
}

/** The brake application that slowed the car for this corner. */
function zone(trace: LapTrace, window: Window): BrakeRun | null {
  if (wraps(window)) return null;
  const d = trace.dist;
  const speed = trace.column('speed');
  let best: BrakeRun | null = null;
  let bestDrop = -Infinity;
  for (const run of trace.runs) {
    const mid = (d[run.on]! + d[run.off]!) / 2;
    if (mid > window.apex) break; // runs are in driving order: the rest are further on still
    if (window.prev_apex !== null && mid <= window.prev_apex) continue;
    if (d[run.on]! < window.entry - BRAKE_SEARCH_M) continue;
    const drop = speed !== null ? speed[run.on]! - speed[run.off]! : run.off - run.on;
    if (drop > bestDrop) {
      best = run;
      bestDrop = drop;
    }
  }
  return best;
}

/** Index of the sample where the car began to turn for this corner. */
function turnIn(trace: LapTrace, window: Window): number | null {
  const yaw = trace.column('yaw_rate');
  if (yaw === null || wraps(window)) return null;
  const [lo, hi] = trace.span(window.entry, window.exit);
  if (hi - lo < 2) return null;
  // Python's max() keeps the first of equals.
  let peak = lo;
  for (let i = lo + 1; i < hi; i++) if (yaw[i]! > yaw[peak]!) peak = i;
  if (yaw[peak]! < TURN_IN_YAW_FLOOR) return null;
  const gate = Math.max(yaw[peak]! * TURN_IN_YAW_SHARE, TURN_IN_YAW_FLOOR);
  let limit = window.apex - TURN_IN_SEARCH_M;
  if (window.prev_apex !== null) limit = Math.max(limit, window.prev_apex);
  const d = trace.dist;
  let i = peak;
  while (i > 0 && yaw[i - 1]! >= gate) {
    i -= 1;
    if (d[i]! < limit) {
      // Still turning this far back: the corner runs on from the one
      // before it, and there is no turn-in of its own to point at.
      return null;
    }
  }
  return i > 0 ? i : null;
}

/** The corner's body slip, turned to read as rotation into the corner. */
function balance(trace: LapTrace, window: Window, out: CornerMeasure): void {
  const slip = trace.column('body_slip');
  if (slip === null || (window.direction !== 'L' && window.direction !== 'R')) return;
  const [lo, hi] = trace.span(window.entry, window.exit);
  if (hi - lo < SLIP_MIN_SAMPLES) return;
  // The channel counts the nose to the RIGHT of travel as positive, which
  // in a right-hander is into the corner and in a left-hander out of it.
  const into = window.direction === 'R' ? 1 : -1;
  let peak = -Infinity;
  let sum = 0;
  for (let i = lo; i < hi; i++) {
    const v = slip[i]! * into;
    if (v > peak) peak = v;
    sum += v;
  }
  out.slip_peak = peak;
  out.slip_mean = sum / (hi - lo);
}

/** The gear GT7 last suggested on the way into this corner.
 *
 * Looked for over the stretch the corner's braking zone may lie in — from
 * BRAKE_SEARCH_M before its entry, never back past the previous apex — up to
 * and including the slowest point. The last suggestion rather than the one
 * at the slowest point, because whether GT7 keeps the hint up until then or
 * clears it once the driver is in the gear it asked for has not been checked
 * against a console; either way it is the gear it asked for.
 */
function heldSuggestion(trace: LapTrace, window: Window, slowest: number): number | null {
  const suggested = trace.column('suggested_gear');
  if (suggested === null) return null;
  let first = bisectLeft(trace.dist, window.entry - BRAKE_SEARCH_M);
  if (window.prev_apex !== null) first = Math.max(first, bisectRight(trace.dist, window.prev_apex));
  for (let i = slowest; i >= first; i--) {
    if (isForwardGear(suggested[i]!)) return Math.trunc(suggested[i]!);
  }
  return null;
}

export function newCornerMeasure(n: number): CornerMeasure {
  return {
    n,
    brake_on: null,
    brake_off: null,
    brake_peak: null,
    brake_on_speed: null,
    brake_off_speed: null,
    turn_in: null,
    brake_at_turn_in: null,
    trail_m: null,
    min_speed: null,
    min_speed_dist: null,
    min_speed_gear: null,
    suggested_gear: null,
    flat: null,
    throttle_on: null,
    throttle_full: null,
    slip_peak: null,
    slip_mean: null,
  };
}

export function measureCorner(trace: LapTrace, window: Window): CornerMeasure {
  const out = newCornerMeasure(window.n);
  if (trace.length < 2 || wraps(window)) return out;
  balance(trace, window, out);
  const d = trace.dist;
  const speed = trace.column('speed');
  const brake = trace.column('brake');
  const throttle = trace.column('throttle');
  const gear = trace.column('gear');

  const run = zone(trace, window);
  if (run !== null && brake !== null) {
    out.brake_off = d[run.off]!;
    let peak = -Infinity;
    for (let i = run.on; i <= run.off; i++) if (brake[i]! > peak) peak = brake[i]!;
    out.brake_peak = peak;
    if (run.onset_known) out.brake_on = d[run.on]!;
    if (speed !== null) {
      out.brake_off_speed = speed[run.off]!;
      if (run.onset_known) out.brake_on_speed = speed[run.on]!;
    }
  }

  const turn = turnIn(trace, window);
  if (turn !== null) {
    out.turn_in = d[turn]!;
    if (brake !== null) {
      out.brake_at_turn_in = brake[turn]!;
      if (run !== null) {
        out.trail_m = run.off >= turn ? Math.max(0, d[run.off]! - d[turn]!) : 0;
      }
    }
  }

  const [lo, hi] = trace.span(window.entry, window.exit);
  if (speed === null || hi - lo <= 0) return out;
  // Python's min() keeps the first of equals.
  let slowest = lo;
  for (let i = lo + 1; i < hi; i++) if (speed[i]! < speed[slowest]!) slowest = i;
  out.min_speed = speed[slowest]!;
  out.min_speed_dist = d[slowest]!;
  if (gear !== null) out.min_speed_gear = Math.trunc(gear[slowest]!);
  out.suggested_gear = heldSuggestion(trace, window, slowest);

  if (throttle !== null) {
    // From where the braking began (or the corner did) to the slowest
    // point: if the throttle never came off through all of that, the
    // corner was flat.
    const liftFrom = run !== null ? run.on : lo;
    let least = Infinity;
    for (let i = Math.min(liftFrom, slowest); i <= slowest; i++) {
      if (throttle[i]! < least) least = throttle[i]!;
    }
    const lifted = least < THROTTLE_ON_PCT;
    out.flat = !lifted;
    if (lifted) {
      let limit = window.exit + THROTTLE_SEARCH_M;
      if (window.next_apex !== null) limit = Math.min(limit, window.next_apex);
      for (let i = slowest; i < d.length; i++) {
        if (d[i]! > limit) break;
        if (out.throttle_on === null && throttle[i]! >= THROTTLE_ON_PCT) out.throttle_on = d[i]!;
        if (throttle[i]! >= THROTTLE_FULL_PCT) {
          out.throttle_full = d[i]!;
          break;
        }
      }
    }
  }
  return out;
}

/** Every corner's measurements for one lap, by corner number. */
export function measure(trace: LapTrace, cornerWindows: readonly Window[]): Map<number, CornerMeasure> {
  const out = new Map<number, CornerMeasure>();
  for (const w of cornerWindows) out.set(w.n, measureCorner(trace, w));
  return out;
}

// --- what two laps did differently -------------------------------------------

/** Metres earlier (negative) or later (positive) than the reference lap.
 *
 * null unless both laps braked for the corner: a lap that took it flat has
 * no braking point to be early or late against.
 */
export function brakePointDelta(
  mine: CornerMeasure | null | undefined,
  theirs: CornerMeasure | null | undefined,
): number | null {
  if (mine == null || theirs == null) return null;
  if (mine.brake_on === null || theirs.brake_on === null) return null;
  return mine.brake_on - theirs.brake_on;
}

/** Metres from the brake going on to its release. null for a corner taken
 *  without braking, and for a zone whose onset is on the lap before. */
export function brakeLength(m: CornerMeasure | null | undefined): number | null {
  if (m == null || m.brake_on === null || m.brake_off === null) return null;
  return m.brake_off - m.brake_on;
}

/** Whether the corner was taken in a higher gear than GT7 suggested for it:
 *  the gear at the slowest point against the held suggestion. null unless
 *  both are known, and for a car in neutral or reverse there (a spin, a
 *  stop), which is in no gear to judge. */
export function gearHigh(m: CornerMeasure | null | undefined): boolean | null {
  if (m == null || m.suggested_gear === null || m.min_speed_gear === null) return null;
  if (!isForwardGear(m.min_speed_gear)) return null;
  return m.min_speed_gear > m.suggested_gear;
}

/** km/h carried at the slowest point, relative to the reference lap. */
export function minSpeedDelta(
  mine: CornerMeasure | null | undefined,
  theirs: CornerMeasure | null | undefined,
): number | null {
  if (mine == null || theirs == null) return null;
  if (mine.min_speed === null || theirs.min_speed === null) return null;
  return mine.min_speed - theirs.min_speed;
}
